import { usePatientRiskGapsWithUIExclusions } from '@ctw/shared/api/fhir/risk-gaps';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { buildPatientRiskGapsColumns } from '@ctw/shared/content/patient-risk-gaps/helpers/columns';
import { RiskGapFilters } from '@ctw/shared/content/patient-risk-gaps/helpers/filters';
import { PeriodDropdown } from '@ctw/shared/content/patient-risk-gaps/helpers/period-dropdown';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import { ResourceTableActions } from '@ctw/shared/content/resource/resource-table-actions';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useFilteredSortedData } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { useMemo, useState } from 'react';
import { defaultRiskSort, riskSortOptions } from './helpers/sorts';

interface PatientRiskGapsProps {
  testId?: string;
}

export const PatientRiskGaps = withErrorBoundary<PatientRiskGapsProps>({
  boundaryName: 'PatientRiskGaps',
  Component: ({ testId }: PatientRiskGapsProps) => {
    const risksQuery = usePatientRiskGapsWithUIExclusions();
    const { openResourceDrawer } = useResourceDrawer();
    const builderConfig = useBuilderConfig();

    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());

    const patientRiskGapsColumns = useMemo(
      () => buildPatientRiskGapsColumns({ builderConfig }),
      [builderConfig],
    );

    const riskGapsFilteredByYear = useMemo(() => {
      if (!risksQuery.data) {
        return [];
      }
      return risksQuery.data.filter((riskGap) => {
        const periodYear = riskGap.period;
        return periodYear === selectedYear;
      });
    }, [risksQuery.data, selectedYear]);

    const { data, filters, setFilters, setSort, defaultSort } = useFilteredSortedData({
      cacheKey: 'patient-risk-gaps',
      defaultSort: defaultRiskSort,
      records: riskGapsFilteredByYear,
    });

    const hasZeroFilteredRecords = !risksQuery.isLoading && data.length === 0;

    useMainContentHeader(
      'PatientRiskGapsContentHeader',
      () => (
        <ResourceTableActions
          headAction={<PeriodDropdown selectedYear={selectedYear} onChange={setSelectedYear} />}
          filterOptions={{
            filters: RiskGapFilters,
            selected: filters,
            onChange: setFilters,
          }}
          sortOptions={{
            defaultSort,
            options: riskSortOptions,
            onChange: setSort,
          }}
        />
      ),
      [filters, defaultSort, selectedYear],
    );

    return (
      <ResourceTable
        title="Patient Risk Gaps"
        titleVisuallyHidden={true}
        showTableHead={true}
        isLoading={risksQuery.isLoading}
        stackedBreakpoint="xs"
        columns={patientRiskGapsColumns}
        data={data}
        testId={testId}
        emptyMessage={
          <EmptyPatientTable
            hasZeroFilteredRecords={hasZeroFilteredRecords}
            resourceName="risk opportunities"
          />
        }
        onRowClick={({ telemetry, record }) =>
          openResourceDrawer({
            telemetry,
            telemetryContext: {
              riskGapType: record.type,
              riskGapStatus: record.status,
              riskGapSubstatuses: record.substatuses,
              riskGapConfidence: record.confidenceLevel,
            },
            model: record,
          })
        }
      />
    );
  },
});
